import { useEffect } from 'react';

import { OptimizelyDecideOption, enums, type ListenerPayload } from '@optimizely/react-sdk';

import { optimizelyClient } from '~/utils/featureflags/FeatureFlagsProvider';
import { FullStoryFeatureFlags } from '~/utils/featureflags/featureFlags';

import { type SetUserVariables } from './useFullStoryUserVars';

type Decision = {
  decisionInfo: {
    flagKey: string;
    variationKey: string;
  };
  type: string;
} & ListenerPayload;

const getFeatureFlags = () => {
  if (!optimizelyClient.isReady()) return null;

  return Object.fromEntries(
    Object.entries(
      // We don't want to send decision events to Optimizely for this call
      optimizelyClient.decideAll([OptimizelyDecideOption.DISABLE_DECISION_EVENT]),
    )
      // Filter out flags that are not enabled for FullStory
      .filter(([key]) => FullStoryFeatureFlags[key])
      .map(([key, value]) => [key, value.variationKey || 'off']),
  );
};

/**
 * Forwards Optimizely's flag decisions to FullStory.
 */
export const useFullStoryFeatureFlags = ({
  setUserVariables,
}: {
  setUserVariables: SetUserVariables;
}) => {
  const isOptimizelyReady = optimizelyClient.isReady();

  // Initial feature flags
  useEffect(() => {
    if (!isOptimizelyReady) return;
    setUserVariables({ featureFlags: getFeatureFlags() });
  }, [isOptimizelyReady, setUserVariables]);

  // Updates feature flags on Optimizely's flag decisions
  useEffect(() => {
    optimizelyClient.notificationCenter.addNotificationListener<Decision>(
      enums.NOTIFICATION_TYPES.DECISION,
      (decision) => {
        if (decision.type !== enums.DECISION_NOTIFICATION_TYPES.FLAG) return;
        const { flagKey, variationKey } = decision.decisionInfo || {};

        if (!flagKey || !variationKey) return;

        // Filter out flags that are not enabled for FullStory
        if (!FullStoryFeatureFlags[flagKey]) return;

        setUserVariables((current) => ({
          ...current,
          featureFlags: {
            ...(current.featureFlags || {}),
            [flagKey]: variationKey,
          },
        }));
      },
    );
  }, [setUserVariables]);
};
